import { useCallback, useMemo, useState } from 'react'

import { createFormMultiSelectCombobox } from '@circlefin/form/Form.MultiSelectCombobox'
import { matchSorter } from 'match-sorter'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

import type { L3OnboardingFieldNameType } from '../../containers/L3OnboardingControl/types'
import type {
  MultiSelectComboboxItem,
  MultiSelectComboboxOptionsLocale,
  MultiSelectComboboxProps,
} from '@circlefin/components/lib/MultiSelectCombobox'

export interface L3OnboardingMultiSelectComboboxProps<T>
  extends Pick<
    MultiSelectComboboxProps<T>,
    'onBlur' | 'onChange' | 'items' | 'label' | 'disabled' | 'warning'
  > {
  /**
   * Field name.
   */
  name: L3OnboardingFieldNameType
}

const MultiSelectCombobox = createFormMultiSelectCombobox()

/**
 *
 */
export function L3OnboardingMultiSelectCombobox<T>({
  name,
  items,
  ...props
}: Readonly<L3OnboardingMultiSelectComboboxProps<T>>) {
  const { t } = useTranslation('l3Onboarding')
  const [filtered, setFiltered] = useState<Array<MultiSelectComboboxItem<T>>>(
    [],
  )
  const [searchTerm, setSearchTerm] = useState('')

  const handleClearInput = useCallback(() => {
    setSearchTerm('')
    setFiltered(items)
  }, [items])

  const handleInputChange = useCallback(
    (search: string) => {
      setSearchTerm(search)
      setFiltered(
        matchSorter(items, search, {
          keys: ['label'],
        }),
      )
    },
    [items, setSearchTerm, setFiltered],
  )

  const locale: MultiSelectComboboxOptionsLocale = useMemo(
    () => ({
      noResultsMessage: (inputValue: string) => (
        <span className="text-black-300">
          <Trans
            components={{
              span: <span className="text-black-400 font-circular-bold" />,
            }}
            i18nKey="common:no-search-match"
            values={{ searchTerm: inputValue }}
          />
        </span>
      ),
      clearButtonLabel: t`common:clear-search`,
    }),
    [t],
  )

  return (
    <MultiSelectCombobox
      dropDirection="down"
      filtered={searchTerm !== '' ? filtered : items}
      items={items}
      locale={locale}
      maxMenuItems={3}
      name={name}
      onClearInput={handleClearInput}
      onInputChange={handleInputChange}
      searchTerm={searchTerm}
      truncate={false}
      {...props}
    />
  )
}
